import { useEffect } from "react";
import { CMS_PREVIEW_EVENT, isCmsPreviewMode } from "./previewUtils";

interface PreviewSelectionPayload {
  path: string;
  type: string;
}

function getPathSpecificity(path: string | undefined): number {
  if (!path) return -1;
  const segmentCount = path.split(".").length;
  return segmentCount * 100 + path.length;
}

function getElementDepth(element: HTMLElement): number {
  let depth = 0;
  let current: HTMLElement | null = element;
  while (current) {
    depth += 1;
    current = current.parentElement;
  }
  return depth;
}

function pickBestEditableTarget(event: MouseEvent, target: HTMLElement | null): HTMLElement | null {
  const candidates: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();

  const addCandidate = (element: Element | null) => {
    const editable = (element as HTMLElement | null)?.closest<HTMLElement>("[data-cms-editable='true']");
    if (!editable || seen.has(editable)) return;
    seen.add(editable);
    candidates.push(editable);
  };

  addCandidate(target);

  const stack = document.elementsFromPoint(event.clientX, event.clientY);
  for (const element of stack) {
    addCandidate(element);
  }

  if (!candidates.length) return null;

  candidates.sort((a, b) => {
    const pathScore = getPathSpecificity(b.dataset.cmsPath) - getPathSpecificity(a.dataset.cmsPath);
    if (pathScore !== 0) return pathScore;

    const aIsMedia = a.dataset.cmsType === "image" || a.dataset.cmsType === "video";
    const bIsMedia = b.dataset.cmsType === "image" || b.dataset.cmsType === "video";
    if (aIsMedia !== bIsMedia) return bIsMedia ? 1 : -1;

    return getElementDepth(b) - getElementDepth(a);
  });

  return candidates[0] ?? null;
}

export default function CmsPreviewBridge() {
  useEffect(() => {
    if (!isCmsPreviewMode()) return;

    document.body.classList.add("cms-preview-active");

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const editable = pickBestEditableTarget(event, target);
      if (!editable) return;

      const path = editable.dataset.cmsPath;
      const type = editable.dataset.cmsType ?? "text";
      if (!path) return;

      event.preventDefault();
      event.stopPropagation();

      const payload: PreviewSelectionPayload = { path, type };
      window.parent.postMessage(
        {
          source: CMS_PREVIEW_EVENT,
          payload,
        },
        window.location.origin,
      );
    };

    document.addEventListener("click", onClick, true);
    return () => {
      document.body.classList.remove("cms-preview-active");
      document.removeEventListener("click", onClick, true);
    };
  }, []);

  return null;
}
